import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Separator } from '../components/ui/separator';
import { useAuth } from '../contexts/AuthContext';
import {
  Search, CheckCircle2, XCircle, RotateCcw, Loader2,
  HandshakeIcon, Ban, Lightbulb, Gavel, ChevronLeft, ChevronRight,
  Filter, ArrowUpDown, Clock, User, Brain, Calendar, FileText, Tag,
  Phone, Mail, MessageSquare, MessageCircle, FolderOpen
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_CONFIG = {
  PROMESSE: { label: 'Promesse', icon: HandshakeIcon, color: 'bg-amber-500/10 text-amber-700 border-amber-300', dot: 'bg-amber-500' },
  BLOCAGE: { label: 'Blocage', icon: Ban, color: 'bg-red-500/10 text-red-700 border-red-300', dot: 'bg-red-500' },
  FAIT_IMPORTANT: { label: 'Fait important', icon: Lightbulb, color: 'bg-blue-500/10 text-blue-700 border-blue-300', dot: 'bg-blue-500' },
  DECISION: { label: 'Décision', icon: Gavel, color: 'bg-violet-500/10 text-violet-700 border-violet-300', dot: 'bg-violet-500' },
};

const STATUS_CONFIG = {
  active: { label: 'Actif', variant: 'default' },
  resolved: { label: 'Résolu', variant: 'secondary' },
  dismissed: { label: 'Ignoré', variant: 'outline' },
};

export default function SuiviPage() {
  const { token, user } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [clones, setClones] = useState([]);
  const limit = 30;

  // Filters
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active');
  const [filterClone, setFilterClone] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');

  // Detail panel
  const [selectedItem, setSelectedItem] = useState(null);

  const canManage = user?.role === 'owner' || user?.role === 'admin';

  const fetchClones = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/persons`, { headers: { Authorization: `Bearer ${token}` } });
      setClones(res.data || []);
    } catch {}
  }, [token]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tracking/stats`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(res.data);
    } catch {}
  }, [token]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType !== 'all') params.append('type', filterType);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      else params.delete('status');
      if (filterClone !== 'all') params.append('person_id', filterClone);
      if (searchText.trim()) params.append('search', searchText.trim());
      params.append('sort_order', sortOrder);
      params.append('page', page);
      params.append('limit', limit);

      const res = await axios.get(`${API}/tracking?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error('Erreur chargement du suivi');
    } finally {
      setLoading(false);
    }
  }, [token, filterType, filterStatus, filterClone, searchText, sortOrder, page]);

  useEffect(() => { fetchClones(); }, [fetchClones]);
  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [filterType, filterStatus, filterClone, searchText, sortOrder]);

  const handleAction = async (itemId, action, e) => {
    if (e) e.stopPropagation();
    setActionLoading(itemId);
    try {
      await axios.patch(`${API}/tracking/${itemId}/${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const newStatus = action === 'resolve' ? 'resolved' : action === 'dismiss' ? 'dismissed' : 'active';
      toast.success(action === 'resolve' ? 'Marqué comme résolu' : action === 'dismiss' ? 'Ignoré' : 'Réouvert');
      // Update selectedItem in-place if it's the one being actioned
      if (selectedItem?.id === itemId) {
        setSelectedItem(prev => prev ? { ...prev, status: newStatus, resolved_at: action === 'resolve' ? new Date().toISOString() : prev.resolved_at } : null);
      }
      fetchItems();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(null);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <DashboardLayout title="Suivi Opérationnel">
      <div className="space-y-5" data-testid="suivi-page">
        {/* Stats Row */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="suivi-stats">
            <StatCard
              label="Promesses"
              value={stats.promesses}
              icon={HandshakeIcon}
              iconColor="text-amber-500"
              bgColor="bg-amber-500/5 hover:bg-amber-500/10"
              active={filterType === 'PROMESSE' && filterStatus === 'active'}
              onClick={() => { setFilterType(filterType === 'PROMESSE' ? 'all' : 'PROMESSE'); setFilterStatus('active'); }}
              testId="stat-promesses"
            />
            <StatCard
              label="Blocages"
              value={stats.blocages}
              icon={Ban}
              iconColor="text-red-500"
              bgColor="bg-red-500/5 hover:bg-red-500/10"
              active={filterType === 'BLOCAGE' && filterStatus === 'active'}
              onClick={() => { setFilterType(filterType === 'BLOCAGE' ? 'all' : 'BLOCAGE'); setFilterStatus('active'); }}
              testId="stat-blocages"
            />
            <StatCard
              label="Faits"
              value={stats.faits}
              icon={Lightbulb}
              iconColor="text-blue-500"
              bgColor="bg-blue-500/5 hover:bg-blue-500/10"
              active={filterType === 'FAIT_IMPORTANT' && filterStatus === 'active'}
              onClick={() => { setFilterType(filterType === 'FAIT_IMPORTANT' ? 'all' : 'FAIT_IMPORTANT'); setFilterStatus('active'); }}
              testId="stat-faits"
            />
            <StatCard
              label="Décisions"
              value={stats.decisions}
              icon={Gavel}
              iconColor="text-violet-500"
              bgColor="bg-violet-500/5 hover:bg-violet-500/10"
              active={filterType === 'DECISION' && filterStatus === 'active'}
              onClick={() => { setFilterType(filterType === 'DECISION' ? 'all' : 'DECISION'); setFilterStatus('active'); }}
              testId="stat-decisions"
            />
            <StatCard
              label="Résolus"
              value={stats.resolved}
              icon={CheckCircle2}
              iconColor="text-emerald-500"
              bgColor="bg-emerald-500/5 hover:bg-emerald-500/10"
              active={filterStatus === 'resolved'}
              onClick={() => { setFilterStatus(filterStatus === 'resolved' ? 'active' : 'resolved'); setFilterType('all'); }}
              testId="stat-resolved"
            />
            <StatCard
              label="Ignorés"
              value={stats.dismissed}
              icon={XCircle}
              iconColor="text-muted-foreground"
              bgColor="bg-muted/30 hover:bg-muted/50"
              active={filterStatus === 'dismissed'}
              onClick={() => { setFilterStatus(filterStatus === 'dismissed' ? 'active' : 'dismissed'); setFilterType('all'); }}
              testId="stat-dismissed"
            />
          </div>
        )}

        {/* Filters Row */}
        <div className="flex flex-wrap items-center gap-3" data-testid="suivi-filters">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher dans le contenu..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="pl-9"
              data-testid="suivi-search"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[150px]" data-testid="filter-type">
              <Filter className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous types</SelectItem>
              <SelectItem value="PROMESSE">Promesses</SelectItem>
              <SelectItem value="BLOCAGE">Blocages</SelectItem>
              <SelectItem value="FAIT_IMPORTANT">Faits</SelectItem>
              <SelectItem value="DECISION">Décisions</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px]" data-testid="filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="active">Actifs</SelectItem>
              <SelectItem value="resolved">Résolus</SelectItem>
              <SelectItem value="dismissed">Ignorés</SelectItem>
            </SelectContent>
          </Select>
          {clones.length > 0 && (
            <Select value={filterClone} onValueChange={setFilterClone}>
              <SelectTrigger className="w-[160px]" data-testid="filter-clone">
                <SelectValue placeholder="Clone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les clones</SelectItem>
                {clones.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
            className="gap-1.5"
            data-testid="sort-toggle"
          >
            <ArrowUpDown className="w-3.5 h-3.5" />
            {sortOrder === 'desc' ? 'Récents' : 'Anciens'}
          </Button>
        </div>

        {/* Items Table */}
        <Card data-testid="suivi-table-card">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[120px]">Type</TableHead>
                  <TableHead>Contenu</TableHead>
                  <TableHead className="w-[140px]">Contact</TableHead>
                  <TableHead className="w-[130px]">Clone</TableHead>
                  <TableHead className="w-[80px] text-center">Jours</TableHead>
                  <TableHead className="w-[100px]">Status</TableHead>
                  <TableHead className="w-[120px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12">
                      <Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                      Aucun élément trouvé avec ces filtres
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => {
                    const cfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.FAIT_IMPORTANT;
                    const Icon = cfg.icon;
                    const statusCfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.active;
                    const isActioning = actionLoading === item.id;

                    return (
                      <TableRow key={item.id} className="group cursor-pointer hover:bg-muted/50" onClick={() => setSelectedItem(item)} data-testid={`tracking-row-${item.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full shrink-0 ${cfg.dot}`} />
                            <Badge variant="outline" className={`text-[11px] px-1.5 py-0 ${cfg.color}`}>
                              <Icon className="w-3 h-3 mr-1" />
                              {cfg.label}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm truncate max-w-[400px]">{item.content}</p>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground truncate max-w-[140px]">
                          {item.contact_name || '-'}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground truncate block max-w-[130px]">
                            {item.person_name || '-'}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Clock className="w-3 h-3 text-muted-foreground" />
                            <span className={`text-xs font-mono ${item.days_active > 14 ? 'text-red-600 font-bold' : item.days_active > 7 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                              {item.days_active}j
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={statusCfg.variant} className="text-[10px]">
                            {statusCfg.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                            {item.status === 'active' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => handleAction(item.id, 'resolve', e)}
                                  disabled={isActioning}
                                  className="h-7 px-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                  data-testid={`resolve-${item.id}`}
                                >
                                  {isActioning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                                  <span className="ml-1 text-xs hidden sm:inline">Résoudre</span>
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => handleAction(item.id, 'dismiss', e)}
                                  disabled={isActioning}
                                  className="h-7 px-2 text-muted-foreground hover:text-foreground"
                                  data-testid={`dismiss-${item.id}`}
                                >
                                  <XCircle className="w-3.5 h-3.5" />
                                  <span className="ml-1 text-xs hidden sm:inline">Ignorer</span>
                                </Button>
                              </>
                            )}
                            {(item.status === 'resolved' || item.status === 'dismissed') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => handleAction(item.id, 'reopen', e)}
                                disabled={isActioning}
                                className="h-7 px-2 text-muted-foreground hover:text-foreground"
                                data-testid={`reopen-${item.id}`}
                              >
                                {isActioning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                                <span className="ml-1 text-xs hidden sm:inline">Réouvrir</span>
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between" data-testid="suivi-pagination">
            <p className="text-sm text-muted-foreground">
              {total} élément{total > 1 ? 's' : ''} — Page {page}/{totalPages}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                data-testid="prev-page"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
                data-testid="next-page"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Sheet */}
      <Sheet open={!!selectedItem} onOpenChange={(open) => { if (!open) setSelectedItem(null); }}>
        <SheetContent side="right" className="sm:max-w-lg overflow-y-auto" data-testid="detail-sheet">
          {selectedItem && <ItemDetailPanel item={selectedItem} token={token} onAction={handleAction} actionLoading={actionLoading} />}
        </SheetContent>
      </Sheet>
    </DashboardLayout>
  );
}

function ItemDetailPanel({ item, token, onAction, actionLoading }) {
  const [context, setContext] = useState(null);
  const [contextLoading, setContextLoading] = useState(true);

  const cfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.FAIT_IMPORTANT;
  const Icon = cfg.icon;
  const statusCfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.active;
  const isActioning = actionLoading === item.id;

  useEffect(() => {
    let cancelled = false;
    setContextLoading(true);
    axios.get(`${API}/tracking/${item.id}/context`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (!cancelled) setContext(res.data); })
      .catch(() => { if (!cancelled) setContext(null); })
      .finally(() => { if (!cancelled) setContextLoading(false); });
    return () => { cancelled = true; };
  }, [item.id, token]);

  const formatDate = (iso) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  const formatShortDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    } catch { return ''; }
  };

  const interactionIcon = (type) => {
    const map = { call: 'phone', email: 'mail', sms: 'message-square', whatsapp: 'message-circle' };
    return map[type] || 'file-text';
  };

  const interactionLabel = (type, direction) => {
    if (type === 'call') return direction === 'outbound' ? 'Appel sortant' : 'Appel entrant';
    if (type === 'email') return direction === 'sent' ? 'Email envoyé' : 'Email reçu';
    if (type === 'sms') return 'SMS';
    if (type === 'whatsapp') return 'WhatsApp';
    return type;
  };

  const contact = context?.contact;
  const dossier = context?.dossier;
  const interactions = context?.recent_interactions || [];
  const relatedItems = context?.related_items || [];

  return (
    <div className="space-y-4" data-testid="detail-panel">
      {/* Header */}
      <SheetHeader>
        <div className="flex items-center gap-2.5">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${cfg.color}`}>
            <Icon className="w-4 h-4" />
          </div>
          <div>
            <SheetTitle className="text-base">{cfg.label}</SheetTitle>
            <SheetDescription>
              <Badge variant={statusCfg.variant} className="text-[10px] mt-0.5">{statusCfg.label}</Badge>
              {item.days_active > 0 && (
                <span className={`ml-2 text-xs font-mono ${item.days_active > 14 ? 'text-red-600 font-bold' : item.days_active > 7 ? 'text-amber-600' : ''}`}>
                  {item.days_active} jour{item.days_active > 1 ? 's' : ''}
                </span>
              )}
            </SheetDescription>
          </div>
        </div>
      </SheetHeader>

      <Separator />

      {/* Content */}
      <div data-testid="detail-content">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Contenu</h4>
        <p className="text-sm leading-relaxed">{item.content}</p>
      </div>

      {/* Context source */}
      {item.context && (
        <div data-testid="detail-context">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Contexte source</h4>
          <div className="bg-muted/40 rounded-lg p-3 border border-border/50">
            <p className="text-sm text-muted-foreground leading-relaxed italic">{item.context}</p>
          </div>
        </div>
      )}

      <Separator />

      {/* Contact section */}
      <div data-testid="detail-contact-section">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Contact</h4>
        {contextLoading ? (
          <div className="flex items-center gap-2 py-2"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /><span className="text-xs text-muted-foreground">Chargement...</span></div>
        ) : contact ? (
          <div className="bg-muted/30 rounded-lg p-3 border border-border/50 space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <User className="w-4 h-4 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{contact.first_name} {contact.last_name}</p>
                {contact.company && <p className="text-xs text-muted-foreground truncate">{contact.company}{contact.city ? ` — ${contact.city}` : ''}{contact.postal_code ? ` (${contact.postal_code})` : ''}</p>}
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground pl-10">
              {contact.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{contact.phone}</span>}
              {contact.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{contact.email}</span>}
            </div>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">{item.contact_name || 'Aucun contact lié'}</p>
        )}
      </div>

      {/* Dossier */}
      {dossier && (
        <div data-testid="detail-dossier">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Dossier</h4>
          <a href={`/dossiers/${dossier.id}`} className="flex items-center gap-2 bg-muted/30 rounded-lg p-2.5 border border-border/50 hover:bg-muted/50 transition-colors">
            <FolderOpen className="w-4 h-4 text-amber-500 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{dossier.title}</p>
              <p className="text-[11px] text-muted-foreground">{dossier.status === 'in_progress' ? 'En cours' : dossier.status === 'closed' ? 'Fermé' : dossier.status}</p>
            </div>
          </a>
        </div>
      )}

      <Separator />

      {/* Metadata row */}
      <div className="grid grid-cols-2 gap-2" data-testid="detail-metadata">
        <MetaItem icon={Brain} label="Clone" value={item.person_name || '-'} />
        <MetaItem icon={Calendar} label="Extrait le" value={formatDate(item.extracted_at)} />
        {item.source_type && <MetaItem icon={FileText} label="Source" value={item.source_type} />}
        {item.confidence != null && <MetaItem icon={Tag} label="Confiance" value={`${Math.round(item.confidence * 100)}%`} />}
      </div>

      {/* Recent interactions timeline */}
      <div data-testid="detail-interactions">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Dernières interactions</h4>
        {contextLoading ? (
          <div className="flex items-center gap-2 py-2"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
        ) : interactions.length > 0 ? (
          <div className="space-y-0 border rounded-lg overflow-hidden">
            {interactions.map((inter, i) => (
              <div key={inter.id || i} className="flex items-start gap-2.5 px-3 py-2 border-b last:border-b-0 hover:bg-muted/30 transition-colors">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${inter.type === 'call' ? 'bg-green-500/10 text-green-600' : inter.type === 'email' ? 'bg-blue-500/10 text-blue-600' : inter.type === 'whatsapp' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-muted text-muted-foreground'}`}>
                  {inter.type === 'call' && <Phone className="w-3 h-3" />}
                  {inter.type === 'email' && <Mail className="w-3 h-3" />}
                  {inter.type === 'sms' && <MessageSquare className="w-3 h-3" />}
                  {inter.type === 'whatsapp' && <MessageCircle className="w-3 h-3" />}
                  {!['call','email','sms','whatsapp'].includes(inter.type) && <FileText className="w-3 h-3" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium">{interactionLabel(inter.type, inter.direction)}</span>
                    <span className="text-[10px] text-muted-foreground shrink-0">{formatShortDate(inter.timestamp)}</span>
                  </div>
                  {inter.summary && <p className="text-xs text-muted-foreground leading-snug mt-0.5 line-clamp-2">{inter.summary}</p>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground py-1">Aucune interaction trouvée</p>
        )}
      </div>

      {/* Related items */}
      {relatedItems.length > 0 && (
        <div data-testid="detail-related">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Autres items du contact</h4>
          <div className="flex flex-wrap gap-1.5">
            {relatedItems.map(r => {
              const rc = TYPE_CONFIG[r.type] || TYPE_CONFIG.FAIT_IMPORTANT;
              return (
                <div key={r.id} className={`text-[11px] px-2 py-1 rounded-md border ${rc.color} max-w-full`}>
                  <span className="font-medium">{rc.label}</span>
                  <span className="text-muted-foreground ml-1 truncate">{r.content.length > 60 ? r.content.slice(0, 60) + '...' : r.content}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Resolution info */}
      {item.status === 'resolved' && item.resolved_at && (
        <>
          <Separator />
          <div data-testid="detail-resolution">
            <div className="bg-emerald-500/5 rounded-lg p-3 border border-emerald-200/30">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span className="text-sm">Résolu le {formatDate(item.resolved_at)}</span>
              </div>
            </div>
          </div>
        </>
      )}

      <Separator />

      {/* Actions */}
      <div className="flex gap-2" data-testid="detail-actions">
        {item.status === 'active' && (
          <>
            <Button
              onClick={(e) => onAction(item.id, 'resolve', e)}
              disabled={isActioning}
              className="flex-1 gap-2 bg-emerald-600 hover:bg-emerald-700"
              data-testid="detail-resolve-btn"
            >
              {isActioning ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Résoudre
            </Button>
            <Button
              variant="outline"
              onClick={(e) => onAction(item.id, 'dismiss', e)}
              disabled={isActioning}
              className="flex-1 gap-2"
              data-testid="detail-dismiss-btn"
            >
              <XCircle className="w-4 h-4" />
              Ignorer
            </Button>
          </>
        )}
        {(item.status === 'resolved' || item.status === 'dismissed') && (
          <Button
            variant="outline"
            onClick={(e) => onAction(item.id, 'reopen', e)}
            disabled={isActioning}
            className="flex-1 gap-2"
            data-testid="detail-reopen-btn"
          >
            {isActioning ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
            Réouvrir
          </Button>
        )}
      </div>
    </div>
  );
}

function MetaItem({ icon: Icon, label, value, highlight }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />
      <div>
        <p className="text-[11px] text-muted-foreground">{label}</p>
        <p className={`text-sm font-medium ${highlight ? 'text-red-600' : ''}`}>{value}</p>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, iconColor, bgColor, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 p-3 rounded-lg border transition-all duration-200 text-left w-full ${bgColor} ${active ? 'ring-2 ring-primary/50 border-primary/30' : 'border-transparent'}`}
      data-testid={testId}
    >
      <Icon className={`w-5 h-5 shrink-0 ${iconColor}`} />
      <div>
        <p className="text-2xl font-bold leading-none">{value}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
      </div>
    </button>
  );
}
